import { Link } from 'react-router-dom';
import { Building2, Clock, CreditCard, RefreshCw, TrendingUp } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminApi, type AdminDashboardKpi } from '@/services/adminApi';
import { useRefreshableFetch } from '@/hooks/admin/useRefreshableFetch';

/**
 * AdminDashboardPage — landing of `/admin` (D1.1 = Dashboard 3-KPI).
 *
 * Three headline tiles (signups today, trials ending soon, MRR proxy)
 * + a recent activity feed of the last 10 cross-tenant company events.
 *
 * The numbers come from `/v1/admin/dashboard/kpi` which queries Firestore
 * live — fast enough for the MVP (~few hundred companies). Heavy
 * pre-computed snapshots arrive in sub-plan 03 if and when scale demands.
 */
export default function AdminDashboardPage() {
  const { data, error, isRefreshing, refresh } = useRefreshableFetch<AdminDashboardKpi>(
    () => adminApi.dashboard.kpi(),
    [],
  );

  if (error) {
    return <div className="text-sm text-destructive">Failed to load dashboard: {error}</div>;
  }
  if (!data) {
    return <div className="text-sm text-muted-foreground">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-tenant snapshot of HiroMatch customers. Numbers refresh on demand.
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={isRefreshing}
          variant="outline"
          size="sm"
          className="gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* 3 KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiTile
          label="Signups today"
          value={data.signupsToday.toString()}
          icon={<Building2 className="h-4 w-4" />}
          hint="New companies created since 00:00 UTC"
        />
        <KpiTile
          label="Trials ending in 7d"
          value={data.trialsEndingNext7d.toString()}
          icon={<Clock className="h-4 w-4" />}
          hint="Active trials reaching trial.endsAt within a week"
        />
        <KpiTile
          label="MRR proxy"
          value={`${data.mrrCurrency} ${data.mrrProxy.toLocaleString('en-US')}`}
          icon={<CreditCard className="h-4 w-4" />}
          hint="Sum of monthly plan price across active companies (no Autumn API hit)"
        />
      </div>

      {/* Recent activity */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">Recent activity</h2>
          </div>
          {data.recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">No recent activity.</p>
          ) : (
            <ul className="divide-y divide-border/30">
              {data.recentActivity.map((row) => (
                <li key={row.companyId + row.at}>
                  <Link
                    to={`/admin/customers/${row.companyId}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-accent/40 transition-colors"
                  >
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                        row.kind === 'signup'
                          ? 'bg-success/10 text-success'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {row.kind}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{row.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {row.segment ?? 'sme'} · {row.planId ?? 'no plan'}
                        {row.trialStatus ? ` · trial ${row.trialStatus}` : ''}
                        {row.isActive === false ? ' · inactive' : ''}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {row.at ? new Date(row.at).toLocaleString('en-US') : '—'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiTile({
  label,
  value,
  icon,
  hint,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  hint: string;
}) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-5">
        <div className="flex items-center justify-between text-muted-foreground">
          <p className="text-xs font-medium uppercase tracking-wide">{label}</p>
          {icon}
        </div>
        <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{hint}</p>
      </CardContent>
    </Card>
  );
}
